import React from "react";
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  title: string;
  summary: string;
  icon: LucideIcon;
  iconColor?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function CollapsibleSection({ 
  title, 
  summary, 
  icon: Icon, 
  iconColor = "text-primary",
  children,
  defaultOpen = false
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Card className="mb-4 rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-5 flex items-start gap-4 text-left hover:bg-muted/30 transition-colors"
      >
        <Icon className={cn("w-5 h-5 mt-0.5 flex-shrink-0", iconColor)} />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-foreground mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{summary}</p>
        </div>
        <div className="flex-shrink-0 ml-2">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <div className="px-5 pb-5 pt-0 border-t border-border/50 animate-fade-in">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
      
      {!isOpen && (
        <div className="px-5 pb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsOpen(true)}
            className="text-xs text-primary hover:text-primary/80 p-0 h-auto"
          >
            View deeper analysis →
          </Button>
        </div>
      )}
    </Card>
  );
}
